/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import { RiAddLine, RiSubtractLine } from '@remixicon/react';
import useImage from 'use-image';
import { stageFrameClass, toolApplyClass, toolCancelClass, toolChipClass } from './editorUi';

interface ZoomToolProps {
  image: string;
  onApply: (newImage: string) => void;
  onClose: () => void;
}

export function ZoomTool({ image, onApply, onClose }: ZoomToolProps) {
  const [zoomPercent, setZoomPercent] = useState(0); // 0% = tamanho da tela, 100% = zoom máximo
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [imageElement] = useImage(image);

  /** Sempre dimensões da bitmap (igual ao canvas drawImage). width/height do DOM podem divergir e quebrar proporção. */
  const imgW = imageElement?.naturalWidth || imageElement?.width || 0;
  const imgH = imageElement?.naturalHeight || imageElement?.height || 0;

  // Converte porcentagem em scale
  const getScaleFromPercent = (percent: number) => {
    if (!imageElement || !imgW || !imgH) return 1;

    const baseScale = Math.min(stageSize.width / imgW, stageSize.height / imgH);
    const maxScale = baseScale * 5; // 5x o tamanho da tela
    
    // 0% = baseScale (tamanho da tela), 100% = maxScale (5x zoom)
    return baseScale + (percent / 100) * (maxScale - baseScale);
  };

  const zoomIn = () => {
    setZoomPercent(prev => Math.min(prev + 10, 100)); // Aumenta 10% por vez
  };

  const zoomOut = () => {
    setZoomPercent(prev => Math.max(prev - 10, 0)); // Diminui 10% por vez
  };

  const resetZoom = () => {
    setZoomPercent(0); // Volta para 0% (tamanho da tela)
    setPosition({ x: 0, y: 0 });
  };

  const applyZoom = () => {
    if (!imageElement || !imgW || !imgH) return;

    try {
      /**
       * Recorte em resolução nativa do que está visível no stage (não um screenshot em baixa res).
       * stage.toDataURL() gerava JPEG no tamanho da prévia (~centenas de px) — a foto “encolhia” no preview.
       */
      const W = imgW;
      const H = imgH;
      const S = getScaleFromPercent(zoomPercent);
      const stageW = stageSize.width;
      const stageH = stageSize.height;

      const imgLeft = position.x;
      const imgTop = position.y;
      const scaledW = W * S;
      const scaledH = H * S;

      const visLeft = Math.max(0, imgLeft);
      const visTop = Math.max(0, imgTop);
      const visRight = Math.min(stageW, imgLeft + scaledW);
      const visBottom = Math.min(stageH, imgTop + scaledH);

      if (visRight <= visLeft || visBottom <= visTop) {
        console.error('Zoom: área visível vazia');
        return;
      }

      let srcX = (visLeft - imgLeft) / S;
      let srcY = (visTop - imgTop) / S;
      let srcW = (visRight - visLeft) / S;
      let srcH = (visBottom - visTop) / S;

      srcX = Math.max(0, srcX);
      srcY = Math.max(0, srcY);
      srcW = Math.min(srcW, W - srcX);
      srcH = Math.min(srcH, H - srcY);
      if (srcW <= 0 || srcH <= 0) return;

      const canvas = document.createElement('canvas');
      const cw = Math.max(1, Math.round(srcW));
      const ch = Math.max(1, Math.round(srcH));
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(imageElement, srcX, srcY, srcW, srcH, 0, 0, cw, ch);

      onApply(canvas.toDataURL('image/jpeg', 0.9));
      onClose();
    } catch (error) {
      console.error('Erro ao aplicar zoom:', error);
    }
  };

  // Calcula o tamanho do stage baseado na imagem
  const stageSize = useMemo(() => {
    if (!imageElement || !imgW || !imgH) return { width: 400, height: 300 };

    const maxWidth = window.innerWidth * 0.8;
    const maxHeight = window.innerHeight * 0.6;

    let width = imgW;
    let height = imgH;
    
    // Redimensiona mantendo proporção
    if (width > maxWidth) {
      height = (height * maxWidth) / width;
      width = maxWidth;
    }
    
    if (height > maxHeight) {
      width = (width * maxHeight) / height;
      height = maxHeight;
    }
    
    return { width, height };
  }, [imageElement, imgW, imgH]);

  // Define a posição inicial quando a imagem carregar
  useEffect(() => {
    if (imageElement && imgW && imgH && position.x === 0 && position.y === 0) {
      const stageWidth = stageSize.width;
      const stageHeight = stageSize.height;

      // Calcula o scale para a imagem caber no stage (0% = tamanho da tela)
      const scaleToFit = Math.min(stageWidth / imgW, stageHeight / imgH);
      const scaledWidth = imgW * scaleToFit;
      const scaledHeight = imgH * scaleToFit;
      
      setPosition({
        x: (stageWidth - scaledWidth) / 2,
        y: (stageHeight - scaledHeight) / 2
      });
    }
  }, [imageElement, imgW, imgH, stageSize, position.x, position.y]);

  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    const currentScale = getScaleFromPercent(zoomPercent);
    
    const mousePointTo = {
      x: (pointer.x - position.x) / currentScale,
      y: (pointer.y - position.y) / currentScale,
    };
    
    // Ajusta a porcentagem baseado no scroll
    const delta = e.evt.deltaY > 0 ? -5 : 5; // 5% por scroll
    const newPercent = Math.max(0, Math.min(100, zoomPercent + delta));
    
    setZoomPercent(newPercent);
    
    const newScale = getScaleFromPercent(newPercent);
    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    
    setPosition(newPos);
  };

  const handleDragEnd = (e: any) => {
    setPosition({
      x: e.target.x(),
      y: e.target.y(),
    });
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative">
      <div className="w-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Stage
              width={stageSize.width}
              height={stageSize.height}
              pixelRatio={1}
              className={`${stageFrameClass} shrink-0`}
              onWheel={handleWheel}
            >
              <Layer>
                {/* Fundo branco */}
                <rect
                  x={0}
                  y={0}
                  width={stageSize.width}
                  height={stageSize.height}
                  fill="white"
                />
                <KonvaImage
                  image={imageElement}
                  x={position.x}
                  y={position.y}
                  width={imgW}
                  height={imgH}
                  scaleX={getScaleFromPercent(zoomPercent)}
                  scaleY={getScaleFromPercent(zoomPercent)}
                  draggable
                  onDragEnd={handleDragEnd}
                />
              </Layer>
            </Stage>
            
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={zoomOut}
                className={toolChipClass}
              >
                <RiSubtractLine className="h-3.5 w-3.5" />
                Diminuir
              </button>

              <span className="min-w-[3rem] text-center text-xs font-bold tabular-nums text-slate-700">
                {zoomPercent}%
              </span>

              <button
                type="button"
                onClick={zoomIn}
                className={toolChipClass}
              >
                <RiAddLine className="h-3.5 w-3.5" />
                Aumentar
              </button>

              <button
                type="button"
                onClick={resetZoom}
                className={toolChipClass}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 absolute bottom-0 left-1/2 -translate-x-1/2">
          <button
            type="button"
            onClick={onClose}
            className={toolCancelClass}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={applyZoom}
            className={toolApplyClass}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}